// React and Library Imports
import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import DatePickerFilter from "../ui-component/DatePickerFilter";
import Drawer from "components/ui-component/SideDrawer";
import Skeleton from "react-loading-skeleton";
import { Menu } from "@headlessui/react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { goToInvalid } from "utils/invalidRoute";
import { useMediaQuery } from "react-responsive";

// Store Hooks and Slices
import { useAppDispatch } from "store/hooks";
import { useUserData } from "store/slices/authSlice";
import { useLeadData, usePortalVanityUrl } from "store/slices/dashboardSlice";
import {
  historyCountGet,
  paymentHistoryGet,
  paymentReceiptDownloadPdf,
  useHistoryCount,
  usePaymentHistory,
} from "store/slices/paymentsSlice";

// Components
import SelectAllDropDown from "components/form/SelectAllDropDown";
import CustomTable from "components/ui-component/CustomTable";
import CustomTooltip from "components/ui-component/CustomTooltip";
import Button from "components/form/Button";
import AmountFilter from "./AmountFilter";
import PaymentPreview from "./PaymentPreview";
import FailedPayment from "./FailedPayment";
import ReAuthDrawer from "./ReAuthDrawer";
import NoPayments from "icons/payments/NoPayments";

// Icons
import { DollarProcessIcon } from "icons/payments/DollarProcessIcon";
import { MoneyReciveIcon } from "icons/payments/MoneyReciveIcon";
import { CloseYellowIcon } from "icons/payments/CloseYellowIcon";
import { GreenTrueIcon } from "icons/payments/GreenTrueIcon";
import { RedCrossIcon } from "icons/payments/RedCrossIcon";
import { DownloadIcon } from "icons/payments/DownloadIcon";
import { MoreIcon } from "icons/payments/MoreIcon";
import { EyeIcon } from "icons/payments/EyeIcon";
import { ClockYellowIcon } from "icons/payments/ClockYellowIcon";
import { RefreshCircle } from "icons/payments/RefreshCircle";
import { TickCircle } from "icons/payments/TickCircle";
import { DangerIcon } from "icons/payments/DangerIcon";
import { TickSquare } from "icons/payments/TickSquare";

// Utils and Constants
import {
  cardToShortname,
  downloadPdfFromApi,
  encode,
  decode,
  formatCurrency,
  formatPaymentDescription,
  getPaymentMethod,
} from "utils/HelperFunctions";
import { dateToString, formatPaymentListDate } from "utils/DateFormat";
import {
  FilterAmountOptions,
  PaymentFilterMethodOptions,
  FilterStatusOptions,
  timeRanges,
} from "utils/Options";

// Types
import { DateState, PaymentFilterTypes } from "types/common";
import { AmountFilterOption } from "types/common";
import { PaymentColumns, PaymentRow } from "types/payments";
import ExcalimIcon from "icons/payments/ExclaimIcon";
import ExclaimIcon2 from "icons/payments/ExclaimIcon2";
import { useTid } from "utils/testid";
import { onActivate } from "utils/a11y";
import { decodeAndValidatePublicLink } from "utils/publicLinkValidation";
import { isPaymentActionType } from "utils/paymentActionRoute";

/* ------------------------------------- PAYMNET TAB ------------------------------------- */

const PaymentsTab: React.FC = () => {
  const tid = useTid();
  const vanityUrl = usePortalVanityUrl();
  const filterScrollRef = useRef<HTMLDivElement>(null);
  const amountFilterRef = useRef<HTMLDivElement>(null);
  const dateFilterRef = useRef<HTMLDivElement>(null);
  const statusFilterRef = useRef<HTMLDivElement>(null);
  const methodFilterRef = useRef<HTMLDivElement>(null);

  // Store and navigation hooks
  const dispatch = useAppDispatch();
  const userData = useUserData();
  const historyCount = useHistoryCount();
  const paymentHistory = usePaymentHistory();
  const accountLeadData = useLeadData();
  const navigate = useNavigate();
  const { clusterId: cluster_id } = useParams();
  const clusterId = decode(cluster_id || "", true);
  const location = useLocation();
  const { state } = location;
  const searchParams = new URLSearchParams(location.search);

  // Responsive queries
  const isLessThan590 = useMediaQuery({ query: "(max-width: 590px)" });
  const isLessThan680 = useMediaQuery({ query: "(max-width: 680px)" });

  // UI and filter state
  const [selectedFilter, setSelectedFilter] = useState<
    "all" | "succeeded" | "failed" | "refunded"
  >(state && state.status ? "failed" : "all");
  const [status, setStatus] = useState<string[]>(
    state && state.status ? ["Failed"] : []
  );
  const [failedPayment, setFailedPayment] = useState(false);
  const [failedPaymentId, setFailedPaymentId] = useState<string | null>(null);
  const [isOrderId, setIsOrderId] = useState<string | null>(null);
  const [isReAuth, setIsReAuth] = useState(false);
  const closeReAuth = () => {
    setIsReAuth(false);
    setIsOrderId(null);
  };
  const [method, setMethod] = useState<string[]>([]);
  const [amount, setAmount] = useState<AmountFilterOption>();
  const [openCal, setOpenCal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPreview, setIsPreview] = useState(false);
  const [isPreviewId, setIsPreviewId] = useState("");
  const [isTableLoading, setIsTableLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState<string | number>("");
  // const [datePickerKey, setDatePickerKey] = useState(0);
  const [filter, setFilter] = useState<PaymentFilterTypes>({
    offset: 0,
    limit: 20,
    method: undefined,
    status: undefined,
    amount: undefined,
    startDate: undefined,
    endDate: undefined,
  });
  const [dateState, setDateState] = useState<DateState>({
    start: "",
    end: "",
  });

  const handleCallback = (start: any, end: any): void => {
    if (filterScrollRef.current && dateFilterRef.current) {
      filterScrollRef.current.scrollTo({
        left:
          dateFilterRef.current.offsetLeft -
          (filterScrollRef.current.offsetLeft || 0),
        behavior: "smooth",
      });
    }
    setTimeout(() => {
      setOpenCal(true);
      setDateState({ start, end });
    }, 250);
    setTimeout(() => {
      ensureActiveBlue();
      installRangesObserver();
    }, 300);
  };

  // Memoized label for date range
  const label = React.useMemo(
    () =>
      dateState?.start || dateState?.end
        ? `${dateState?.start?.format(
            "MMM D, YYYY"
          )} - ${dateState?.end?.format("MMM D, YYYY")}`
        : null,
    [dateState]
  );

  // Utility: highlight active date range in blue
  const ensureActiveBlue = React.useCallback((): void => {
    const dropdown = document.querySelector(".daterangepicker");
    const allLis = dropdown?.querySelectorAll(
      ".ranges li"
    ) as NodeListOf<HTMLElement> | null;
    const allAnchors = dropdown?.querySelectorAll(
      ".ranges li a"
    ) as NodeListOf<HTMLElement> | null;
    allLis?.forEach((el) => el.style.removeProperty("color"));
    allAnchors?.forEach((el) => el.style.removeProperty("color"));
    const activeLi = dropdown?.querySelector(
      ".ranges li.active"
    ) as HTMLElement | null;
    if (activeLi)
      activeLi.style.setProperty("color", "var(--color-blue)", "important");
    const activeAnchor = dropdown?.querySelector(
      ".ranges li.active a, .ranges li > a.active"
    ) as HTMLElement | null;
    if (activeAnchor)
      activeAnchor.style.setProperty("color", "var(--color-blue)", "important");
  }, []);

  // Utility: observe range changes for blue highlight
  const installRangesObserver = React.useCallback((): void => {
    const dropdown = document.querySelector(".daterangepicker");
    const ranges = dropdown?.querySelector(".ranges");
    if (!ranges) return;
    const observer = new MutationObserver(() => {
      ensureActiveBlue();
    });
    observer.observe(ranges, {
      attributes: true,
      subtree: true,
      attributeFilter: ["class"],
    });
    setTimeout(() => observer.disconnect(), 2000);
  }, [ensureActiveBlue]);

  // Date picker show/hide handlers
  const handleShow = React.useCallback((): void => {
    if (!dateState?.start && !dateState?.end) {
      const dropdown = document.querySelector(".daterangepicker");
      const active = dropdown?.querySelector(".ranges li.active");
      if (active) active.classList.remove("active");
    }
    setTimeout(() => {
      const dropdown = document.querySelector(".daterangepicker");
      dropdown?.classList.add("payments-drp");
      ensureActiveBlue();
      installRangesObserver();
    }, 0);
  }, [dateState, ensureActiveBlue, installRangesObserver]);

  const handleHide = React.useCallback((): void => {
    const dropdown = document.querySelector(".daterangepicker");
    dropdown?.classList.remove("payments-drp");
  }, []);

  // Data fetching handlers
  const onGetHistoryCount = React.useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      const payload = { accounts_id: userData?.data?.accountData?.id };
      await dispatch(historyCountGet(payload)).unwrap();
    } catch (error: any) {
      console.log("get count err: -->", error?.message);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, userData]);

  const onGetPaymentHistory = React.useCallback(async (): Promise<void> => {
    setIsTableLoading(true);
    try {
      const payload = {
        ...filter,
        accounts_id: userData?.data?.accountData?.id,
      };
      await dispatch(paymentHistoryGet(payload)).unwrap();
    } catch (error: any) {
      console.log("get payment history err: -->", error?.message);
    } finally {
      setIsTableLoading(false);
    }
  }, [dispatch, filter, userData]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    onGetHistoryCount();
  }, []);

  useEffect(() => {
    onGetPaymentHistory();
    if (state) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // Download payment receipt handler
  const onPaymentDownload = React.useCallback(
    async (
      id: string,
      receiptId?: any,
      isWire: boolean = false
    ): Promise<void> => {
      const pid = encode(`${id}_${clusterId}_${new Date().getTime()}`, "pid_");
      const payload = {
        invoice_id: id,
        lead_id: accountLeadData?.lead_id,
        url: `${vanityUrl}view-payment/${pid}`,
        cluster_id: clusterId,
        isFromPortal: true,
      };
      await downloadPdfFromApi(
        () => dispatch(paymentReceiptDownloadPdf(payload)).unwrap(),
        `${isWire ? "Wire transfer request" : "Receipt"}  - ${receiptId || id}.pdf`,
        (loading) => setIsDownloading(loading ? id : "")
      );
    },
    [accountLeadData, clusterId, dispatch]
  );

  console.log("status", status);

  const selectedSlot = useMemo(() => {
    if (status.length) {
      if (status.length === 1 && status.includes("Succeeded")) {
        return "succeeded";
      } else if (
        status.length === 3 &&
        status.includes("Failed") &&
        status.includes("Disputed") &&
        status.includes("Voided")
      ) {
        return "failed";
      } else if (
        status.length === 2 &&
        status.includes("Refunded") &&
        status.includes("Partial refund")
      ) {
        return "refunded";
      }
      return "";
    } else {
      return "all";
    }
  }, [selectedFilter, status]);

  useEffect(() => {
    if (selectedFilter === "all" && !status?.length) return;

    switch (selectedFilter) {
      case "succeeded":
        setStatus(["Succeeded"]);
        break;
      case "failed":
        setStatus(["Failed", "Disputed", "Voided"]);
        break;
      case "refunded":
        setStatus(["Refunded", "Partial refund"]);
        break;
      case "all":
        setStatus([]);
        break;
      default:
        setStatus([]);
        setOpenCal(false);
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter]);

  useEffect(() => {
    const methodFormatted = method?.map((data) => cardToShortname(data));
    if (!status?.length && !state) {
      setSelectedFilter("all");
    }
    // Keep the same object when nothing actually changed, so the fetch effect
    // below doesn't re-run on mount (React bails out when the updater returns
    // the previous state).
    setFilter((prev) => {
      const next = {
        ...prev,
        offset: 0,
        method: method.length > 0 ? JSON.stringify(methodFormatted) : undefined,
        status: status.length > 0 ? JSON.stringify(status) : undefined,
        amount: amount ? JSON.stringify(amount) : undefined,
        startDate: dateState?.start
          ? dateToString(dateState?.start)
          : undefined,
        endDate: dateState?.end ? dateToString(dateState?.end) : undefined,
      };
      const unchanged = (Object.keys(next) as (keyof typeof next)[]).every(
        (key) => next[key] === prev[key]
      );
      return unchanged ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, method, dateState, amount]);

  // Open a payment action after its public link has passed through login.
  // Keep the older failedOrderId URL for payment provider callbacks.
  useEffect(() => {
    const drawer = searchParams.get("drawer");
    const actionType = searchParams.get("paymentActionType");
    const actionId = searchParams.get("paymentActionId");
    const hasAction = actionType !== null || actionId !== null;

    if (drawer === "open" && hasAction) {
      const publicLink = decodeAndValidatePublicLink(actionId, "pid_");
      if (
        !isPaymentActionType(actionType) ||
        !publicLink ||
        publicLink.clusterId !== clusterId
      ) {
        goToInvalid(navigate, "link");
        return;
      }

      if (actionType === "failed") {
        setFailedPaymentId(actionId);
        setFailedPayment(true);
      } else {
        setIsOrderId(actionId);
        setIsReAuth(true);
      }

      // Once the drawer is open, remove only the deep-link handoff values.
      // Payment providers append their own callback query to this page later.
      const nextSearch = new URLSearchParams(location.search);
      nextSearch.delete("drawer");
      nextSearch.delete("paymentActionType");
      nextSearch.delete("paymentActionId");
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch.toString() ? `?${nextSearch.toString()}` : "",
        },
        { replace: true }
      );
      return;
    }

    const failedOrderId = searchParams.get("failedOrderId");

    if (!drawer || !failedOrderId || failedPayment) {
      return;
    }

    const publicLink = decodeAndValidatePublicLink(failedOrderId, "pid_");
    if (!publicLink || publicLink.clusterId !== clusterId) {
      goToInvalid(navigate, "link");
      return;
    }

    setFailedPayment(true);
    setFailedPaymentId(failedOrderId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns: PaymentColumns[] = [
    {
      id: "insert_date",
      name: "Payment Date",
      minWidth: "130px",
      cell: (row: any) => {
        return (
          <div>
            {isTableLoading ? (
              <Skeleton width={100} />
            ) : row?.payment_date || row?.insert_date ? (
              formatPaymentListDate(
                row?.payment_date || row?.insert_date,
                userData?.data?.accountData?.timezone,
                "short"
              )
            ) : (
              "--"
            )}
          </div>
        );
      },
    },
    {
      id: "amount",
      name: "Amount",
      minWidth: "130px",
      cell: (row: any) => {
        return (
          <div className="text-xl font-medium text-txtPrimary leading-normal">
            {isTableLoading ? (
              <Skeleton width={100} />
            ) : row?.amount ? (
              formatCurrency(
                row?.amount +
                  (row?.payment_method === "ACH" ? row?.ach_fee_amount : 0) +
                  (row?.payment_method !== "ACH" ? row?.cc_fee_amount : 0)
              ) || "$0.00"
            ) : (
              "--"
            )}
          </div>
        );
      },
    },
    {
      id: "card_type",
      name: "Payment Method",
      minWidth: "150px",
      cell: (row: any) => {
        return (
          <div className="w-full text-left">
            {isTableLoading ? (
              <Skeleton width={120} />
            ) : row?.payment_method ? (
              getPaymentMethod({
                card_type: row?.card_type,
                method: row?.payment_method,
                digits: row?.card_last_4_digits,
                account_number: row?.card_last_4_digits,
                className: "",
                textClassName: "",
                isHidden: false,
                ivr_type: row?.ivr_type,
                bank_name: row?.bank_name,
                rowId: row?.id
              })
            ) : (
              "--"
            )}
          </div>
        );
      },
    },
    {
      id: "payment_status",
      name: "Status",
      minWidth: "200px",
      cell: (row: any) => {
        let status = row?.payment_status
          ? row?.payment_status?.toLowerCase()
          : null;

        return (
          <div className="flex gap-[8px] items-center relative">
            {isTableLoading ? (
              <Skeleton width={150} />
            ) : status ? (
              <>
                <div
                  className={`pointer-events-none flex text-sm items-center py-[3px] px-[10px] gap-[6px] rounded-md ${
                    status === "succeeded" || status === "disputed won"
                      ? "bg-[#23B3390D] text-green"
                      : status === "failed" || status === "disputed lost"
                        ? "bg-[#FF00000D] text-red"
                        : status === "refunded"
                          ? "bg-[#1678F20D] text-blue"
                          : status === "voided" || status === "pending"
                            ? "bg-[#FDB6000D] text-yellow"
                            : status === "partial refund"
                              ? "bg-[#F3F8FE] text-blue"
                              : status === "disputed"
                                ? "bg-[#4F576A0D] text-iconBright"
                                : status === "disputed lost"
                                  ? "bg-[#FF00000D] text-red"
                                  : status === "disputed won"
                                    ? "bg-[#23B3390D] text-green"
                                    : status === "incomplete"
                                      ? "bg-[#4F576A0D] text-iconBright "
                                      : ""
                  }`}
                >
                  {status === "succeeded" || status === "disputed won" ? (
                    <GreenTrueIcon />
                  ) : status === "failed" || status === "disputed lost" ? (
                    <RedCrossIcon />
                  ) : status === "refunded" ? (
                    <MoneyReciveIcon />
                  ) : status === "voided" ? (
                    <CloseYellowIcon />
                  ) : status === "partial refund" ? (
                    <DollarProcessIcon />
                  ) : status === "disputed" ? (
                    <ExclaimIcon2 strokeColor="var(--color-icon-bright)" />
                  ) : status === "pending" ? (
                    <ClockYellowIcon />
                  ) : status === "disputed lost" ? (
                    <DangerIcon />
                  ) : status === "disputed won" ? (
                    <TickSquare />
                  ) : status === "incomplete" ? (
                    <ExcalimIcon />
                  ) : (
                    ""
                  )}
                  {row?.payment_status === "Disputed lost"
                    ? "Failed"
                    : row?.payment_status === "Disputed won"
                      ? "Succeeded"
                      : row?.payment_status}
                </div>
                <>
                  {status === "failed" && (
                    <Button
                      text="Pay"
                      data-testid={tid(`payment-row-${row?.id}-pay-button`)}
                      aria-label={`Pay failed payment ${row?.id}`}
                      className="text-sm cursor-pointer w-fit !py-1 !px-2 rounded-md text-white !leading-[15.62px] font-medium focus:outline-none"
                      onClick={() => {
                        let encodeString = encode(
                          `${row?.id}_${clusterId}_${new Date().getTime()}`,
                          "pid_"
                        );
                        setFailedPayment(true);
                        setFailedPaymentId(encodeString);
                      }}
                    />
                  )}
                  {row?.customerid > 0 && row?.payment_method !== "WTO" ? (
                    <>
                      <CustomTooltip
                        maxwidth="300px"
                        id={`paymentTab-custom${row?.id}`}
                        place="top"
                        content={`<div class="font-normal text-xs leading-[18px] whitespace-nowrap">Recurring payment</div>`}
                      >
                        <div
                          id={`paymentTab-custom${row?.id}`}
                          className="w-fit cursor-pointer relative"
                        >
                          <RefreshCircle />
                        </div>
                      </CustomTooltip>
                    </>
                  ) : (
                    <></>
                  )}
                  {row?.recovered_payment && row?.recovered_payment === "Y" && (
                    <>
                      <CustomTooltip
                        maxwidth="264px"
                        place="top"
                        id={`payment-custom2${row.id}`}
                        content={`<div class="font-normal text-xs leading-[18px] whitespace-nowrap">Recovered payment${
                          row?.recovered_date
                            ? `<br/>Original payment date: ${formatPaymentListDate(
                                row?.insert_date,
                                userData?.data?.accountData?.timezone,
                                "short"
                              )}`
                            : ""
                        }</div>`}
                      >
                        <div
                          id={`payment-custom2${row.id}`}
                          className="w-fit cursor-pointer relative"
                        >
                          <TickCircle />
                        </div>
                      </CustomTooltip>
                    </>
                  )}
                </>
              </>
            ) : (
              "--"
            )}
          </div>
        );
      },
    },
    {
      id: "payment_description",
      name: "Payment Type",
      minWidth: "150px",
      cell: (row: any) => {
        let desc =
          row?.payment_type_label || row?.payment_note || row?.description;
        let description =
          desc === "Custom plan"
            ? "Custom Plan"
            : row?.ivr_type === "MANUALPAY"
              ? "Manual Payment"
              : formatPaymentDescription(desc);
        return (
          <div className="pointer-events-none">
            {isTableLoading ? (
              <Skeleton width={150} />
            ) : description ? (
              <div className="text-sm font-normal leading-[18.23px] text-txtPrimary">
                {description || "--"}
              </div>
            ) : (
              "--"
            )}
          </div>
        );
      },
    },
    {
      id: "id",
      name: "",
      right: "true",
      minWidth: "120px",
      cell: (row: any) => {
        const isWire =
          row?.payment_status === "Pending" && row?.payment_method === "WTO";
        return isTableLoading ? (
          <></>
        ) : (
          <div className="flex items-center gap-5">
            <div className="items-center gap-5 hidden icon-group">
              <div
                role="button"
                tabIndex={0}
                aria-label={`Preview payment ${row?.id}`}
                data-testid={tid(`payment-row-${row?.id}-preview-button`)}
                onClick={() => {
                  setIsPreview(true);
                  setIsPreviewId(row?.id);
                }}
                onKeyDown={onActivate(() => {
                  setIsPreview(true);
                  setIsPreviewId(row?.id);
                })}
              >
                <EyeIcon
                  className="group"
                  _className="group-hover:!fill-iconObscure"
                />
              </div>
              <div
                role="button"
                tabIndex={0}
                aria-label={`Download payment ${row?.id}`}
                data-testid={tid(`payment-row-${row?.id}-download-button`)}
                onClick={() =>
                  onPaymentDownload(row?.id, row?.receiptId, isWire)
                }
                onKeyDown={onActivate(() =>
                  onPaymentDownload(row?.id, row?.receiptId, isWire)
                )}
              >
                {isDownloading === row?.id ? (
                  <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
                ) : (
                  <DownloadIcon
                    className="group"
                    _className="group-hover:!stroke-iconObscure"
                  />
                )}
              </div>
            </div>
            <Menu as="div" className="relative">
              <Menu.Button
                aria-label={`Open actions for payment ${row?.id}`}
                data-testid={tid(`payment-row-${row?.id}-actions-button`)}
              >
                <div className="hover:!bg-bgHover p-[9px] rounded-full">
                  <MoreIcon
                    className="group"
                    _className="group-hover:!fill-iconObscure"
                  />
                </div>
              </Menu.Button>

              <Menu.Items className="absolute w-[153px] -left-[130px] top-[35px] !bg-bgPrimary hover:!bg-bgPrimary z-[1] rounded-[7px] drop-shadow-lg">
                <Menu.Item
                  as="div"
                  data-testid={tid(`payment-row-${row?.id}-preview-menu-item`)}
                  className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary rounded-t-[7px]"
                  onClick={() => {
                    setIsPreview(true);
                    setIsPreviewId(row?.id);
                  }}
                >
                  <EyeIcon width="16" height="16" /> Preview
                </Menu.Item>
                <Menu.Item
                  as="div"
                  data-testid={tid(`payment-row-${row?.id}-download-menu-item`)}
                  className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary rounded-b-[7px]"
                  onClick={() =>
                    onPaymentDownload(row?.id, row?.receiptId, isWire)
                  }
                >
                  {isDownloading === row?.id ? (
                    <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
                  ) : (
                    <DownloadIcon width="16" height="16" />
                  )}{" "}
                  {isDownloading === row?.id ? "Downloading..." : "Download"}
                </Menu.Item>
              </Menu.Items>
            </Menu>
          </div>
        );
      },
    },
  ];

  // Only claim the customer has no payments when the list agrees with the
  // counter. The counter can under-report (statuses it doesn't classify are
  // dropped), and showing "no payments" to someone who has them is far worse
  // than showing the table.
  if (
    historyCount &&
    historyCount?.all === 0 &&
    (paymentHistory?.totalCount || 0) === 0
  ) {
    return (
      <div className="text-center h-full grid place-items-center lg:pb-[43px] lg:px-0 px-5">
        <div className="h-fit w-fit flex items-center flex-col justify-center gap-10">
          <NoPayments
            width={window.innerWidth < 500 ? 80 : 100}
            height={window.innerWidth < 500 ? 80 : 100}
          />
          <div>
            <p className="lg:text-2xl text-xl text-txtPrimary font-semibold mb-4">
              No payments found
            </p>
            <p className="lg:text-lg text-base text-txtParagraph">
              Looks like there are no payments here. Want to process a new
              payment?
            </p>
          </div>
          <Button
            text="Make Payment"
            data-testid={tid("empty-make-payment-button")}
            aria-label="Make a payment"
            className="lg:!px-11 lg:!py-[10px] !px-4 !py-2"
            onClick={() => navigate(`/${cluster_id}/make-payment`)}
          />
        </div>
      </div>
    );
  }

  const GetRow = (dataRow?: PaymentRow) => {
    const isWire =
      dataRow?.payment_status === "Pending" &&
      dataRow?.payment_method === "WTO";

    let desc = dataRow?.payment_note || dataRow?.description;
    let description =
      desc === "Custom Plan"
        ? "Custom Plan"
        : dataRow?.ivr_type === "MANUALPAY"
          ? "Manual Payment"
          : formatPaymentDescription(desc);
    return (
      <div
        role="button"
        tabIndex={0}
        aria-label={`Preview payment ${dataRow?.id ?? ""}`}
        data-testid={tid(`payment-mobile-row-${dataRow?.id ?? "unknown"}`)}
        className="w-full py-3 flex flex-col items-start justify-start gap-[10px] relative"
        onClick={() => {
          setIsPreview(true);
          setIsPreviewId(`${dataRow?.id ?? ""}`);
        }}
        onKeyDown={onActivate(() => {
          setIsPreview(true);
          setIsPreviewId(`${dataRow?.id ?? ""}`);
        })}
      >
        <div className="flex justify-between items-center w-full">
          <span className="text-xl font-medium leading-[26px] text-txtPrimary">
            {formatCurrency(
              (dataRow?.amount ?? 0) +
                (dataRow?.payment_method === "ACH"
                  ? (dataRow?.ach_fee_amount ?? 0)
                  : 0) +
                (dataRow?.payment_method !== "ACH"
                  ? (dataRow?.cc_fee_amount ?? 0)
                  : 0)
            ) || "$0.00"}
          </span>
          <span className="flex items-center justify-end gap-2">
            <div
              className={`pointer-events-none flex items-center py-[3px] px-[10px] gap-[6px] rounded-md !text-sm ${
                dataRow?.payment_status?.toLowerCase() === "succeeded"
                  ? "bg-[#23B3390D] text-green"
                  : dataRow?.payment_status?.toLowerCase() === "failed"
                    ? "bg-[#FF00000D] text-red"
                    : dataRow?.payment_status?.toLowerCase() === "refunded"
                      ? "bg-[#1678F20D] text-blue"
                      : dataRow?.payment_status?.toLowerCase() === "voided" ||
                          dataRow?.payment_status?.toLowerCase() === "pending"
                        ? "bg-[#FDB6000D] text-yellow"
                        : dataRow?.payment_status?.toLowerCase() ===
                            "partial refund"
                          ? "bg-[#F3F8FE] text-blue"
                          : dataRow?.payment_status?.toLowerCase() ===
                              "disputed"
                            ? "bg-[#4F576A0D] text-iconBright"
                            : dataRow?.payment_status?.toLowerCase() ===
                                "disputed lost"
                              ? "bg-[#FF00000D] text-red"
                              : dataRow?.payment_status?.toLowerCase() ===
                                  "disputed won"
                                ? "bg-[#23B3390D] text-green"
                                : dataRow?.payment_status?.toLowerCase() ===
                                    "incomplete"
                                  ? "bg-[#4F576A0D] text-iconBright "
                                  : ""
              }`}
            >
              {dataRow?.payment_status?.toLowerCase() === "succeeded" ||
              dataRow?.payment_status?.toLowerCase() === "disputed won" ? (
                <GreenTrueIcon />
              ) : dataRow?.payment_status?.toLowerCase() === "failed" ||
                dataRow?.payment_status?.toLowerCase() === "disputed lost" ? (
                <RedCrossIcon />
              ) : dataRow?.payment_status?.toLowerCase() === "refunded" ? (
                <MoneyReciveIcon />
              ) : dataRow?.payment_status?.toLowerCase() === "voided" ? (
                <CloseYellowIcon />
              ) : dataRow?.payment_status?.toLowerCase() ===
                "partial refund" ? (
                <DollarProcessIcon />
              ) : dataRow?.payment_status?.toLowerCase() === "disputed" ? (
                <ExclaimIcon2 strokeColor="var(--color-icon-bright)" />
              ) : dataRow?.payment_status?.toLowerCase() === "pending" ? (
                <ClockYellowIcon />
              ) : dataRow?.payment_status?.toLowerCase() === "disputed lost" ? (
                <DangerIcon />
              ) : dataRow?.payment_status?.toLowerCase() === "disputed won" ? (
                <TickSquare />
              ) : dataRow?.payment_status?.toLowerCase() === "incomplete" ? (
                <ExcalimIcon />
              ) : (
                ""
              )}
              {dataRow?.payment_status === "Disputed lost"
                ? "Failed"
                : dataRow?.payment_status === "Disputed won"
                  ? "Succeeded"
                  : dataRow?.payment_status}
            </div>
            {typeof dataRow?.customerid === "number" &&
            dataRow.customerid > 0 &&
            dataRow?.payment_method !== "WTO" ? (
              <>
                <CustomTooltip
                  maxwidth="300px"
                  id={`paymentTab-custom${dataRow?.id}`}
                  place="top"
                  content={`<div class="font-normal text-xs leading-[18px]" > Recurring payment</div>`}
                >
                  <div
                    id={`paymentTab-custom${dataRow?.id}`}
                    className="w-fit cursor-pointer relative"
                  >
                    <RefreshCircle />
                  </div>
                </CustomTooltip>
              </>
            ) : (
              <></>
            )}
            {dataRow?.recovered_payment &&
              dataRow?.recovered_payment === "Y" && (
                <>
                  <CustomTooltip
                    maxwidth="264px"
                    place="top"
                    id={`payment-custom2${dataRow.id}`}
                    content={`<div class="font-normal text-xs leading-[18px]" > Recovered payment${
                      dataRow?.recovered_date
                        ? `<br/>Original payment date: ${formatPaymentListDate(
                            dataRow?.insert_date,
                            userData?.data?.accountData?.timezone,
                            "short"
                          )}`
                        : ""
                    }</div>`}
                  >
                    <div
                      id={`payment-custom2${dataRow.id}`}
                      className="w-fit cursor-pointer relative"
                    >
                      <TickCircle />
                    </div>
                  </CustomTooltip>
                </>
              )}
            <Menu as="div" className="relative grid place-items-center">
              <Menu.Button
                disabled={isTableLoading}
                aria-label={`Open actions for payment ${dataRow?.id ?? ""}`}
                data-testid={tid(
                  `payment-mobile-row-${dataRow?.id ?? "unknown"}-actions-button`
                )}
                onClick={(e) => e.stopPropagation()}
              >
                <div>
                  <MoreIcon
                    className="group"
                    _className="group-hover:!fill-iconObscure"
                  />
                </div>
              </Menu.Button>

              <Menu.Items className="absolute w-[153px] -left-[130px] top-[30px] !bg-bgPrimary hover:!bg-bgPrimary z-[1] rounded-[7px] drop-shadow-lg">
                <Menu.Item
                  as="div"
                  data-testid={tid(
                    `payment-mobile-row-${dataRow?.id ?? "unknown"}-preview-menu-item`
                  )}
                  className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary rounded-t-[7px]"
                  onClick={() => {
                    setIsPreview(true);
                    setIsPreviewId(`${dataRow?.id ?? ""}`);
                  }}
                >
                  <EyeIcon width="16" height="16" /> Preview
                </Menu.Item>
                <Menu.Item
                  as="div"
                  data-testid={tid(
                    `payment-mobile-row-${dataRow?.id ?? "unknown"}-download-menu-item`
                  )}
                  className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary rounded-b-[7px]"
                  onClick={() =>
                    dataRow?.id &&
                    onPaymentDownload(
                      `${dataRow.id ?? ""}`,
                      dataRow?.receiptId,
                      isWire
                    )
                  }
                >
                  {isDownloading === dataRow?.id ? (
                    <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
                  ) : (
                    <DownloadIcon width="16" height="16" />
                  )}{" "}
                  {isDownloading === dataRow?.id
                    ? "Downloading..."
                    : "Download"}
                </Menu.Item>
              </Menu.Items>
            </Menu>
          </span>
        </div>
        <div className="w-[85%] flex flex-col gap-[10px]">
          <div className="w-full flex gap-[10px] items-center text-txtParagraph text-sm leading-[18px]">
            <span className="w-[120px] shrink-0">Payment method</span>
            <span className="min-w-0 flex-1">
              {getPaymentMethod({
                card_type: dataRow?.card_type ?? "",
                method: dataRow?.payment_method ?? "",
                digits: dataRow?.card_last_4_digits ?? "",
                account_number: dataRow?.account_number ?? "",
                isHidden: false,
                bank_name: dataRow?.bank_name ?? "",
                rowId: dataRow?.id
              })}
            </span>
          </div>
          <div className="w-full flex gap-[10px] items-center text-txtParagraph text-sm leading-[18px]">
            <span className="w-[120px] shrink-0">Date</span>
            <span className="min-w-0 flex-1 text-txtPrimary">
              {formatPaymentListDate(
                dataRow?.payment_date ?? dataRow?.insert_date ?? "",
                userData?.data?.accountData?.timezone,
                "short"
              )}
            </span>
          </div>
          <div className="w-full flex gap-[10px] items-center text-txtParagraph text-sm leading-[18px]">
            <span className="w-[120px] shrink-0">Payment type</span>
            <span className="min-w-0 flex-1 text-txtPrimary">
              {description
                ? description
                : dataRow?.ivr_type === "MANUALPAY"
                  ? "Manual Payment"
                  : "--"}
            </span>
          </div>
        </div>
        {dataRow?.payment_status?.toLowerCase() === "failed" && (
          <span className="absolute right-0 bottom-10">
            <Button
              text="Pay"
              data-testid={tid(
                `payment-mobile-row-${dataRow?.id ?? "unknown"}-pay-button`
              )}
              aria-label={`Pay failed payment ${dataRow?.id ?? ""}`}
              className="text-base cursor-pointer w-fit !py-[6px] !px-[16px] rounded-md text-white !leading-6 font-medium focus:outline-none"
              onClick={() => {
                let encodeString = encode(
                  `${dataRow?.id}_${clusterId}_${new Date().getTime()}`,
                  "pid_"
                );
                setFailedPayment(true);
                setFailedPaymentId(encodeString);
              }}
            />
          </span>
        )}
      </div>
    );
  };

  return (
    <div
      className="lg:px-10 lg:pt-10 h-full lg:overflow-y-auto"
      data-testid={tid("payments-tab")}
    >
      <div
        className="flex items-center lg:gap-[28px] gap-5 lg:flex-nowrap flex-wrap lg:px-0 px-5 lg:overflow-x-auto"
        data-testid={tid("payments-summary-filters")}
      >
        <div
          role="button"
          tabIndex={0}
          aria-pressed={selectedSlot === "all"}
          aria-label="Show all payments"
          data-testid={tid("summary-filter-all")}
          className={`${
            selectedSlot === "all" ? "border-blue border-[1.5px]" : "border-strokePrimary border lg:border-[1.5px]"
          } p-2.5 sm:py-4 sm:pl-4 sm:pr-2 lg:p-4 rounded-xl bg-bgPrimary cursor-pointer lg:w-full sm:w-[calc(50%-10px)] w-full lg:min-w-[170px] h-[93px] lg:h-[100px] hover:border-blue flex flex-col justify-between items-start`}
          onClick={() => {
            setSelectedFilter("all");
            setStatus([]);
          }}
          onKeyDown={onActivate(() => {
            setSelectedFilter("all");
            setStatus([]);
          })}
        >
          <div className="lg:text-2xl text-xl leading-[26px] lg:leading-normal font-medium text-txtPrimary whitespace-nowrap lg:whitespace-normal">
            All
          </div>
          <div className="text-lg leading-[23px] lg:leading-normal text-txtSecondary animate__animated animate__fadeIn">
            {isLoading ? (
              <Skeleton width={27} height={23} />
            ) : (
              historyCount?.all || 0
            )}
          </div>
        </div>
        <div
          role="button"
          tabIndex={0}
          aria-pressed={selectedSlot === "succeeded"}
          aria-label="Show succeeded payments"
          data-testid={tid("summary-filter-succeeded")}
          className={`${
            selectedSlot === "succeeded"
              ? "border-blue border-[1.5px]"
              : "border-strokePrimary border lg:border-[1.5px]"
          } p-2.5 sm:py-4 sm:pl-4 sm:pr-2 lg:p-4 rounded-xl bg-bgPrimary cursor-pointer lg:w-full sm:w-[calc(50%-10px)] w-full lg:min-w-[170px] h-[93px] lg:h-[100px] hover:border-blue flex flex-col justify-between items-start`}
          onClick={() => {
            setSelectedFilter("succeeded");
          }}
          onKeyDown={onActivate(() => setSelectedFilter("succeeded"))}
        >
          <div className="lg:text-2xl text-xl leading-[26px] lg:leading-normal font-medium text-txtPrimary whitespace-nowrap lg:whitespace-normal">
            Succeeded
          </div>
          <div className="text-lg leading-[23px] lg:leading-normal text-txtSecondary animate__animated animate__fadeIn">
            {isLoading ? (
              <Skeleton width={27} height={23} />
            ) : (
              historyCount?.Succeeded || 0
            )}
          </div>
        </div>
        <div
          role="button"
          tabIndex={0}
          aria-pressed={selectedSlot === "failed"}
          aria-label="Show failed payments"
          data-testid={tid("summary-filter-failed")}
          className={`${
            selectedSlot === "failed" ? "border-blue border-[1.5px]" : "border-strokePrimary border lg:border-[1.5px]"
          } p-2.5 sm:py-4 sm:pl-4 sm:pr-2 lg:p-4 rounded-xl bg-bgPrimary cursor-pointer lg:w-full sm:w-[calc(50%-10px)] w-full lg:min-w-[170px] h-[93px] lg:h-[100px] hover:border-blue flex flex-col justify-between items-start`}
          onClick={() => {
            setSelectedFilter("failed");
          }}
          onKeyDown={onActivate(() => setSelectedFilter("failed"))}
        >
          <div className="lg:text-2xl text-xl leading-[26px] lg:leading-normal font-medium text-txtPrimary whitespace-nowrap lg:whitespace-normal">
            Failed
          </div>
          <div className="text-lg leading-[23px] lg:leading-normal text-txtSecondary animate__animated animate__fadeIn">
            {isLoading ? (
              <Skeleton width={27} height={23} />
            ) : (
              historyCount?.Failed || 0
            )}
          </div>
        </div>
        <div
          role="button"
          tabIndex={0}
          aria-pressed={selectedSlot === "refunded"}
          aria-label="Show refunded payments"
          data-testid={tid("summary-filter-refunded")}
          className={`${
            selectedSlot === "refunded" ? "border-blue border-[1.5px]" : "border-strokePrimary border lg:border-[1.5px]"
          } p-2.5 sm:py-4 sm:pl-4 sm:pr-2 lg:p-4 rounded-xl bg-bgPrimary cursor-pointer lg:w-full sm:w-[calc(50%-10px)] w-full h-[93px] lg:h-[100px] lg:min-w-[170px] hover:border-blue flex flex-col justify-between items-start`}
          onClick={() => {
            setSelectedFilter("refunded");
          }}
          onKeyDown={onActivate(() => setSelectedFilter("refunded"))}
        >
          <div className="lg:text-2xl text-xl leading-[26px] lg:leading-normal font-medium text-txtPrimary whitespace-nowrap lg:whitespace-normal">
            Refunded
          </div>
          <div className="text-lg leading-[23px] lg:leading-normal text-txtSecondary animate__animated animate__fadeIn">
            {isLoading ? (
              <Skeleton width={27} height={23} />
            ) : (
              historyCount?.refundCount || 0
            )}
          </div>
        </div>
      </div>
      <div
        ref={filterScrollRef}
        data-testid={tid("payments-filter-bar")}
        className="pt-[30px] flex flex-wrap items-center gap-2.5 w-full px-5 lg:flex-nowrap lg:gap-3 lg:overflow-x-scroll lg:px-0 scrollbar-hide"
      >
        <div className="w-[calc(50%-5px)] min-w-0 lg:w-fit" ref={dateFilterRef}>
          <DatePickerFilter
            dateState={dateState}
            timeRanges={timeRanges}
            handleCallback={handleCallback}
            handleShow={handleShow}
            handleHide={handleHide}
            openCal={openCal}
            setOpenCal={setOpenCal}
            setDateState={setDateState}
            label={label}
            onOpen={() => {
              if (filterScrollRef.current && dateFilterRef.current) {
                filterScrollRef.current.scrollTo({
                  left: 0,
                  behavior: "smooth",
                });
              }
            }}
          />
        </div>
        <div
          className="w-[calc(50%-5px)] min-w-0 lg:w-fit"
          ref={amountFilterRef}
        >
          <AmountFilter
            options={FilterAmountOptions}
            amount={amount}
            setAmount={setAmount}
            label="Amount"
            onOpen={() => {
              if (filterScrollRef.current && amountFilterRef.current) {
                filterScrollRef.current.scrollTo({
                  left:
                    amountFilterRef.current.offsetLeft -
                    (filterScrollRef.current.offsetLeft || 0),
                  behavior: "smooth",
                });
              }
            }}
          />
        </div>
        <div
          className="w-[calc(50%-5px)] min-w-0 lg:w-fit"
          ref={statusFilterRef}
        >
          <SelectAllDropDown
            options={FilterStatusOptions}
            checked={status}
            setChecked={setStatus}
            label="Status"
            onOpen={() => {
              if (filterScrollRef.current && statusFilterRef.current) {
                filterScrollRef.current.scrollTo({
                  left:
                    statusFilterRef.current.offsetLeft -
                    (filterScrollRef.current.offsetLeft || 0),
                  behavior: "smooth",
                });
              }
            }}
          />
        </div>
        <div
          className="w-[calc(50%-5px)] min-w-0 lg:w-fit"
          ref={methodFilterRef}
        >
          <SelectAllDropDown
            options={PaymentFilterMethodOptions}
            checked={method}
            setChecked={setMethod}
            label="Payment Method"
            onOpen={() => {
              if (filterScrollRef.current && methodFilterRef.current) {
                filterScrollRef.current.scrollTo({
                  left:
                    methodFilterRef.current.offsetLeft -
                    (filterScrollRef.current.offsetLeft || 0),
                  behavior: "smooth",
                });
              }
            }}
          />
        </div>
      </div>
      <div className="pt-[30px]">
        <CustomTable
          data-testid={tid("payments-table")}
          ariaLabel="Payments table"
          getRowKey={(row: PaymentRow, index: number) => row?.id || index}
          getRowTestId={(row: PaymentRow, index: number) =>
            tid(`payment-mobile-row-wrapper-${row?.id || index}`)
          }
          columns={columns}
          data={paymentHistory?.paymentHistoryData || []}
          fixedHeaderScrollHeight="calc(var(--shell-vh) - 452px)"
          perPage={filter?.limit}
          currentPage={(filter?.offset + 20) / 20}
          totalCount={paymentHistory?.totalCount || 0}
          isTableLoading={isTableLoading}
          onNextClick={(e) => {
            setFilter({
              ...filter,
              offset: filter?.offset + 20,
            });
          }}
          onPreviousClick={(e) => {
            setFilter({
              ...filter,
              offset: filter?.offset - 20,
            });
          }}
          GetRow={GetRow}
          onRowClick={(row: any, e: any) => {
            setIsPreview(true);
            setIsPreviewId(row?.id);
          }}
        />
      </div>
      <div>
        <Drawer
          enableOverlay={true}
          lockBackgroundScroll={true}
          direction="right"
          open={isPreview}
          size={isLessThan590 ? `100vw` : "590px"}
          onClose={() => {
            setIsPreview(false);
            setIsPreviewId("");
          }}
          overlayColor="#000"
        >
          <div className="contents" data-testid={tid("payment-preview-drawer")}>
            <PaymentPreview
              id={isPreviewId}
              setId={setIsPreviewId}
              show={isPreview}
              setShow={setIsPreview}
              onDownloadCLick={(
                id: any,
                receiptId: any,
                isWire: boolean = false
              ) => onPaymentDownload(id, receiptId, isWire)}
              isDownloading={isDownloading?.toString()}
            />
          </div>
        </Drawer>
        <Drawer
          enableOverlay={true}
          lockBackgroundScroll={true}
          direction="right"
          open={failedPayment}
          size={`${isLessThan680 ? `100vw` : "680px"}`}
          // The width check that used to guard this meant the panel could not
          // be dismissed at all on a phone, where it covers the whole screen —
          // the same fix already applied to the dashboard's copy of this drawer.
          onClose={() => {
            setFailedPayment(false);
            setFailedPaymentId(null);
          }}
          overlayColor="#000"
        >
          <div className="contents" data-testid={tid("failed-payment-drawer")}>
            <FailedPayment
              id={failedPaymentId}
              show={failedPayment}
              setShow={setFailedPayment}
              setId={setFailedPaymentId}
              fetchDetails={() => {
                onGetPaymentHistory();
                onGetHistoryCount();
              }}
            />
          </div>
        </Drawer>
        <Drawer
          enableOverlay={true}
          lockBackgroundScroll={true}
          direction="right"
          open={isReAuth}
          size={`${isLessThan680 ? `100vw` : "680px"}`}
          onClose={closeReAuth}
          overlayColor="#000"
          aria-label="Payment re-authentication"
        >
          {isOrderId && (
            <ReAuthDrawer
              key={isOrderId}
              id={isOrderId}
              accountId={userData?.data?.accountData?.accountid}
              clusterId={userData?.data?.accountData?.clusterid || clusterId}
              leadId={accountLeadData?.lead_id}
              onClose={closeReAuth}
            />
          )}
        </Drawer>
      </div>
    </div>
  );
};

export default PaymentsTab;
